<template>
  <footer class="pure-g pure-u-1">
    <div class="fullsizeimage text-center">
      <div class="hidden-img md:block pure-u-1 fullsizeimage__wave-overlay">
        <Wave
          class="w-full"
          :alt="$tv('waveAltText', 'home')"
        />
      </div>
    </div>
    <div class="footer pure-g -my-2 section--blue-dark">
      <div class="pure-u-1 pure-u-md-1-3 px-sm md:ml-auto md:w-25 footer-section">
        <div>{{ $tv('Sharetext', 'footer') }}</div>
        <div class="share-section">
          <div
            class="icon-wrapper"
            v-for="(icon, index) in footerIcons"
            :key="index"
          >
            <div>
              <a
                :href="icon.url"
                rel="nofollow noopener"
                class="link--no-decoration link link--white"
              >
                <span
                  class="k-icon"
                  :class="icon.icon"
                />
              </a>
            </div>
          </div>
          <div class="share-section__xing">
            <a
              href="https://www.xing.com/pages/fst"
              rel="nofollow noopener"
              class="link--no-decoration link link--white"
            >
              <XingIcon />
            </a>
          </div>
        </div>
      </div>
      <div class="pure-u-1 pure-u-md-1-3 px-sm md:w-25 footer-section">
        <ul>
          <li
            v-for="(item, index) in footerItemsLeft"
            :key="index"
          >
            <a
              :href="item.url[$i18n.locale]"
              target="_blank"
              rel="noopener"
            >
              {{ $tv(item.label, 'footer') }}
            </a>
          </li>
        </ul>
      </div>
      <div class="pure-u-1 pure-u-md-1-3 px-sm md:w-25 md:mr-auto footer-section">
        <ul>
          <li
            v-for="(item, index) in footerItemsRight"
            :key="index"
          >
            <a
              :href="item.url[$i18n.locale]"
              target="_blank"
              rel="noopener"
            >
              {{ $tv(item.label, 'footer') }}
            </a>
          </li>
        </ul>
      </div>
      <div class="pure-u-1 pure-u-md-1-3 px-sm md:w-25 footer-section footer-align-left">
        <div class="contact-us-header">
          {{ $tv('contactUs', 'footer') }}
        </div>
        <ul>
          <li
            v-for="(item, index) in footerItemsFarRight"
            :key="index"
            class="footer-item"
          >
            <span
              v-if="item.icon"
              class="item-icon"
            >
              <component
                :is="item.icon"
                class="want-to-talk-icons"
              />
            </span>
            <a
              class="contact-us-link"
              href="#"
              @click.prevent="handleContactUsClick()"
            >
              {{ $tv(item.label, 'footer') }}
            </a>
          </li>
        </ul>
        <div class="footer__language">
          <details class="footer__language__wrapper">
            <summary
              class="footer__language__summary"
            >
              <span>{{ currentLanguage }}</span>
              <NavigationExpandMore
                class="footer__language__chevron"
                aria-hidden="true"
              />
            </summary>
            <ul class="footer__language__list">
              <li
                v-for="(lang, index) in availableLocales"
                :key="index"
              >
                <Nuxt-Link
                  :href="`${BASE_URL}/${currentRegion}/${lang}`"
                >
                  {{ $tv(lang, 'locales') }}
                </Nuxt-Link>
              </li>
            </ul>
          </details>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import Wave from '~/assets/images/wave.svg?component'
import XingIcon from '~/assets/icons/icon-xing.svg?component'
import CommunicationEmail from '~/assets/icons/communication_email.svg?component'
import Feedback from '~/assets/icons/feedback.svg?component'
import NavigationExpandMore from '~/assets/icons/navigation_expand_more.svg?component'

export default defineComponent({
  name: 'FooterSection',
  components: {
    Wave,
    XingIcon,
    CommunicationEmail,
    Feedback,
    NavigationExpandMore
  },
  setup() {
    const region = useNuxtApp().$globalization.getRegion()
    const {locales: i18nLocales} = useI18n()

    const locales = ref([])

    if (Array.isArray(i18nLocales.value)) {
      locales.value = i18nLocales.value.map(locale =>
        typeof locale === 'string' ? locale : locale.code
      )
    } else {
      locales.value = Object.keys(i18nLocales.value || {})
    }
    if (!locales.value.length) {
      locales.value = ['de', 'en', 'it', 'fr', 'es']
    }

    return {
      BASE_URL: useRuntimeConfig().public.BASE_URL,
      locales,
      currentRegion: typeof region === 'string' ? region.toLowerCase() : 'eu'
    }
  },
  data() {
    return {
      footerItemsLeft: [
        {'label': 'Imprint', 'url': {'de': 'https://www.fst.com/de/footer/impressum/', 'en': 'https://www.fst.com/footer/legal-notice/', 'it': 'https://www.fst.com/footer/legal-notice/', 'fr': 'https://www.fst.com/footer/legal-notice/', 'es': 'https://www.fst.com/footer/legal-notice/'}},
        {'label': 'Cookies', 'url': {'de': 'https://www.fst.com/de/footer/cookies/', 'en': 'https://www.fst.com/footer/cookies/', 'it': 'https://www.fst.com/footer/cookies/', 'fr': 'https://www.fst.com/footer/cookies/', 'es': 'https://www.fst.com/footer/cookies/'}},
        {'label': 'Group', 'url': {'de': 'https://www.fst.com/de/footer/group/', 'en': 'https://www.fst.com/footer/group/', 'it': 'https://www.fst.com/footer/group/', 'fr': 'https://www.fst.com/footer/group/', 'es': 'https://www.fst.com/footer/group/'}}
      ],
      footerItemsRight: [
        {'label': 'Privacy', 'url': {'de': 'https://www.fst.com/de/footer/datenschutzerklaerung/', 'en': 'https://www.fst.com/footer/data-protection-regulations/', 'it': 'https://www.fst.com/footer/data-protection-regulations/', 'fr': 'https://www.fst.com/footer/data-protection-regulations/', 'es': 'https://www.fst.com/footer/data-protection-regulations/'}},
        {'label': 'Terms', 'url': {'de': 'https://www.fst.com/de/footer/allgemeine-geschaeftsbedingungen/', 'en': 'https://www.fst.com/footer/terms-and-conditions/', 'it': 'https://www.fst.com/footer/terms-and-conditions/', 'fr': 'https://www.fst.com/footer/terms-and-conditions/', 'es': 'https://www.fst.com/footer/terms-and-conditions/'}}
      ],
      footerIcons: [
        {'icon': 'k-i-linkedin', 'url': 'https://www.linkedin.com/company/fst?'},
        {'icon': 'k-i-youtube', 'url': 'http://www.youtube.com/channel/UCxx9lK0ANlIjO6gAaaYRjgg'}
      ],
      footerItemsFarRight: [
        {'label': 'Contact', 'icon': 'CommunicationEmail', 'url': null}
        // {'label': 'Feedback', 'icon': 'Feedback', 'url': null} // Wont be used for now just left here to be ready when they want it back #38416 - #38460
      ]
    }
  },

  methods: {
    handleContactUsClick() {
      this.$eventbus.emit('showContactPopup')
    }
  },
  computed: {
    currentLanguage() {
      return this.$tv(this.$i18n.locale, 'locales')
    },
    availableLocales() {
      return this.locales.filter(locale => locale !== this.$i18n.locale)
    }
  }
})
</script>

<style lang="scss" scoped>
.section--blue-dark {
  background-color: $color__primary;
  color: $color__white;
}

.fullsizeimage img,
.fullsizeimage picture {
  width: 100%;
}

ul {
  margin-bottom: $default-margin-padding-width;
  list-style: none;
  padding: 0;
}

.hidden-img {
  display: none;
}

footer {
  .footer {
    position: relative;
    z-index: 1;
    padding-top: 2rem;

    .link--no-decoration {
      height: 1.75rem;
      display: block;
    }

    @include breakpoint-up($md) {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }

    .share-section {
      display: flex;
      justify-content: left;

      &__xing {
        font-size: 1.75rem;
        margin-top: 1.5rem;

        @include breakpoint-down($md) {
          margin-top: 0;
        }
      }
    }

    .icon-wrapper {
      display: block;
      padding: 0;

      .k-icon {
        font-size: 1.75rem;
      }
    }

    ul {
      margin: 0;
    }

    ul,
    li {
      padding: .0625rem 0;

      a {
        text-decoration: none;
        font-weight: $base-text-font-weight-semi-bold;
        color: $color__white;
        line-height: 1.5rem;
        transition: box-shadow .2s ease-in-out;

        &:hover {
          box-shadow: inset 0 -.0625rem 0 0 $color--white;
        }
      }
    }
  }

  a:focus-visible {
    outline: .125rem solid $color__white !important;
    outline-offset: .125rem;
    border-radius: .125rem;
  }

  .monsterheadline {
    font-weight: $base-text-font-weight-bold;
    text-transform: uppercase;
    padding: 0;
    margin: 0 0 1.5rem;
  }

  .footer-section {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    padding-bottom: 1.5rem;

    @include breakpoint-up($md) {
      align-items: center;
    }

    .item-icon {
      padding-top: .375rem;

      .icon.icon--svg {
        width: 1.25rem;
        height: 1.25rem;
      }
    }

    .contact-us-header {
      padding-bottom: .5rem;
      padding-right: 1.25rem;
    }

    .footer-item {
      display: flex;
      align-items: center;
      gap: .4rem;

      .contact-us-link {
        line-height: 1.125rem;
      }
    }

    .want-to-talk-icons {
      .icon.icon--svg {
        fill: $color__fst__error;
        width: 1.75rem;
        height: 1.75rem;
      }
    }

  }
}

@include breakpoint-down($md) {
  .footer {
    flex-flow: column wrap;
    padding-left: 1rem;
    padding-bottom: 2.5rem;

    div {
      font-weight: $base-text-font-weight-semi-bold;
      font-size: .9375rem;

      .share-section {
        gap: 1rem;
        margin-top: 1.5rem;
        padding-bottom: .625rem;

        .icon-wrapper {
          padding-right: .5rem;
        }
      }
    }
  }
}

@include breakpoint-up($md) {
  .lg\:px-64 {
    padding-left: 16rem;
    padding-right: 16rem;
  }

  footer {
    .footer {
      .icon-wrapper {
        margin: 1.5rem 0 0;
        padding-right: $default-margin-padding-width;
      }
    }

    .monsterheadline {
      font-size: 5rem;
      color: $color__white;
      line-height: 5rem;
    }
  }

  .fullsizeimage {
    position: relative;
    display: block;
    background-size: cover;
    background-position: 50%;
    background-repeat: no-repeat;
    width: 100%;
    height: 0;
  }

  .fullsizeimage__wave-overlay {
    position: absolute;
    bottom: 0;
    pointer-events: none;

    svg.icon--svg {
      width: 100%;
    }
  }
}

.footer-section.footer-align-left {
  @include breakpoint-up($md) {
    align-items: flex-start;
  }
}

.footer__language {
  margin-top: .5rem;
  position: relative;
  min-height: 2.5rem;
  user-select: none;

  &__wrapper {
    background-color: $color__primary;
    position: relative;
    border: .0625rem solid $color__white;
    padding: .5rem;
    width: 10rem;
    cursor: pointer;
    z-index: 10;
  }

  ul {
    display: none;
    position: absolute;
    bottom: 100%;
    left: -.0625rem;
    width: 100%;
    margin-bottom: .25rem;
    border: .0625rem solid $color__white;
    background-color: $color__primary;
    padding-top: .25rem;
    padding-bottom: .25rem;
    max-height: 12.5rem;
    overflow-y: auto;
    flex-direction: column-reverse;
  }

  li {
    width: 100%;
  }

  details[open] > ul {
    margin-top: 0;
    display: flex;
    flex-direction: column-reverse;
  }

  a {
    background-color: $color__primary;
    display: block;
    padding: .25rem .5rem;
  }

  summary {
    list-style: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: .5rem;
  }

  summary::-webkit-details-marker {
    display: none;
  }

  &__summary:focus-visible {
    outline: .125rem solid $color__white;
    outline-offset: .125rem;
    border-radius: .125rem;
  }

  &__chevron {
    width: 1.5rem;
    height: 1.5rem;
  }
}
</style>
